import { useCallback, useRef } from 'react';

import { useTracker, type TrackingEvent } from '@archipro-website/tracker';
import { reportingDevice } from '~/modules/tracking/util/reporting-device';

import type { QuestionnaireEvent } from './types';

/**
 * What each engine event is called in the warehouse, supplied by the flow.
 *
 * Named per flow rather than once for the engine. The engine is a shape many
 * features will be built on, so a shared `Questionnaire*` name would put every
 * future flow's rows under the same `EVENT_TYPE` — and a chart keyed on that
 * name would quietly start including a feature that did not exist when it was
 * written. The flow is already the `Source`, but `EVENT_TYPE` is the coarse
 * filter most queries reach for first, so it has to be the one that separates
 * them.
 *
 * The engine still decides *when* each event fires and what it carries, so
 * every flow reports the same shape under its own names.
 */
export type QuestionnaireEventNames = Record<
    QuestionnaireEvent['type'],
    TrackingEvent
>;

/**
 * Anything the flow knows that the engine does not.
 *
 * The engine is deliberately ignorant of what it is asking about, so a fact
 * like which button opened it cannot reach it as an event field. It rides here
 * instead and is merged into every event, which is also the only way it stays
 * attached across the whole funnel rather than only to the open.
 */
export type QuestionnaireTrackingContext = Record<
    string,
    string | number | boolean | undefined
>;

/**
 * Bridges engine events onto the tracker under the flow's own event names.
 *
 * `questionnaireId` identifies the flow one-for-one and travels as `Source`, so
 * it is the series key on every chart. Everything else rides inside `ExtraData`:
 * only those two custom keys survive the warehouse pipeline.
 *
 * Pass the result as the engine's `onEvent`. The context is read through a ref
 * so a caller may pass a fresh object every render: the engine reads `onEvent`
 * through a ref of its own, so this identity is not what holds its one-shot
 * reporting still, and making it depend on the context's contents would buy
 * nothing.
 *
 * Every event carries the device, because the questions, the file attachment
 * and the account wall all behave differently on a small screen and a single
 * blended drop-off curve would hide it. Read per event rather than once at the
 * open, so a reader who rotates is reported on the layout they actually
 * answered each step on.
 */
export const useQuestionnaireTracking = (
    eventNames: QuestionnaireEventNames,
    context?: QuestionnaireTrackingContext,
): ((event: QuestionnaireEvent) => void) => {
    const tracker = useTracker();

    const contextRef = useRef(context);
    contextRef.current = context;
    const namesRef = useRef(eventNames);
    namesRef.current = eventNames;

    return useCallback(
        (event: QuestionnaireEvent) => {
            if (typeof window === 'undefined') return;

            const { type, questionnaireId, ...rest } = event;
            const payload = {
                questionnaireId,
                ...rest,
                ...contextRef.current,
                device: reportingDevice(),
            };

            void tracker.log(namesRef.current[type], {
                url: new URL(window.location.href),
                data: {
                    ...payload,
                    Source: questionnaireId,
                    ExtraData: JSON.stringify(payload),
                },
            });
        },
        [tracker],
    );
};
